import {
  FormStepConfig,
  StepRoutingConfig,
  StepRoutingRule,
  StepRouteOperator,
} from '../models/form.model';
import { FormValues } from './condition-evaluator';

/** Parsea el JSON de stepsConfig. Devuelve [] si es inválido o vacío (inc22). */
export function parseStepsConfig(raw: string | null | undefined): FormStepConfig[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((s) => s && typeof s.step === 'number')
        .map((s) => ({
          step: s.step,
          title: s.title ?? '',
          description: s.description ?? '',
          imageUrl: s.imageUrl ?? '',
        }));
    }
    return [];
  } catch {
    return [];
  }
}

/** Config de un paso concreto (o undefined). */
export function stepConfigFor(
  steps: FormStepConfig[],
  step: number,
): FormStepConfig | undefined {
  return steps.find((s) => s.step === step);
}

/** Parsea el JSON de stepRoutingConfig. Devuelve null si es inválido (inc23). */
export function parseStepRoutingConfig(
  raw: string | null | undefined,
): StepRoutingConfig | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.rules)) {
      return {
        rules: parsed.rules.filter(
          (r: StepRoutingRule) =>
            typeof r?.fromStep === 'number' && typeof r?.goToStep === 'number',
        ),
        defaultNext: parsed.defaultNext ?? undefined,
      };
    }
    return null;
  } catch {
    return null;
  }
}

function testRoute(rule: StepRoutingRule, values: FormValues): boolean {
  const raw = values[rule.fieldKey];
  const val = Array.isArray(raw) ? raw.join(',') : (raw ?? '');
  const op: StepRouteOperator = rule.operator;
  switch (op) {
    case 'equals':
      return val === (rule.value ?? '');
    case 'not_equals':
      return val !== (rule.value ?? '');
    case 'contains':
      return !!rule.value && val.includes(rule.value);
    case 'not_empty':
      return val.trim().length > 0;
    case 'is_empty':
      return val.trim().length === 0;
    default:
      return false;
  }
}

/**
 * Resuelve el siguiente paso desde `currentStep` evaluando las reglas de salto
 * condicional (inc23). Prioridad: primera regla que aplica → defaultNext →
 * currentStep + 1. Nunca excede totalSteps.
 */
export function resolveNextStep(
  routing: StepRoutingConfig | null,
  currentStep: number,
  values: FormValues,
  totalSteps: number,
): number {
  if (routing) {
    const match = routing.rules.find(
      (r) => r.fromStep === currentStep && testRoute(r, values),
    );
    if (match) return clamp(match.goToStep, 1, totalSteps);

    const def = routing.defaultNext?.[String(currentStep)];
    if (typeof def === 'number') return clamp(def, 1, totalSteps);
  }
  return clamp(currentStep + 1, 1, totalSteps);
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
